'use client';
import { useEffect, useState } from 'react';

import { useQuery } from '@lichtblick/apollo';
import { useTranslation } from '@lichtblick/i18next';
import {
  GetWorkOrderFilesPayloadModel,
  GetWorkOrderFiles_V1Query,
  GetWorkOrderFiles_V1QueryVariables,
  GetWorkOrder_V1Query,
  GetUserUserPayloadModel,
  TreeNode,
} from '@lichtblick/types';
import { transformToTreeNode, useLogoutOnApolloError } from '@lichtblick/utils';

import { GET_WORK_ORDER_FILES } from './graphql/getWorkOrderFiles';

import { useFileFilters } from '../FileBrowser/hooks/useFileFilters';
import { FolderTreeView } from '../FolderTreeView';
import { SkeletonComponent } from '../Skeleton';

type FileBrowserProps = {
  /** Pre-fetched current user. Passed through to FolderTreeView. */
  currentUser?: GetUserUserPayloadModel;
  /** Pre-fetched query result. When provided, the internal useQuery is skipped. */
  filesData?: GetWorkOrderFiles_V1Query;
  /** External loading state. Only used when filesData is provided. */
  isLoading?: boolean;
  /** Loading state for externally provided workOrderData. Passed through to FolderTreeView. */
  isLoadingWorkOrderData?: boolean;
  isMonday?: boolean;
  isShowingSideBar?: boolean;
  /** External refetch callback. Only used when filesData is provided. */
  refetchFiles?: () => void;
  /** Pre-fetched work order data. Passed through to FolderTreeView. */
  workOrderData?: GetWorkOrder_V1Query['getWorkOrder_V1']['data'];
  workOrderId: number;
};

export const FileBrowser = ({
  currentUser,
  filesData,
  isLoading: externalIsLoading,
  isLoadingWorkOrderData,
  isMonday,
  isShowingSideBar,
  refetchFiles,
  workOrderData,
  workOrderId,
}: FileBrowserProps) => {
  const useExternalData = filesData !== undefined;

  const { fileFilters } = useFileFilters();
  const { t } = useTranslation();
  const [structure, setStructure] = useState<TreeNode[]>([]);
  const {
    data: internalData,
    error: internalError,
    loading: internalIsLoading,
    refetch: internalRefetch,
  } = useQuery<GetWorkOrderFiles_V1Query, GetWorkOrderFiles_V1QueryVariables>(GET_WORK_ORDER_FILES, {
    variables: {
      input: {
        workOrderId,
        includeUnsafeFiles: true,
        includeFiles: true,
      },
    },
    fetchPolicy: 'network-only',
    skip: useExternalData,
  });

  const data = useExternalData ? filesData : internalData;
  const isLoading = useExternalData ? (externalIsLoading ?? false) : internalIsLoading;
  const refetch = useExternalData ? (refetchFiles ?? (() => {})) : internalRefetch;
  const workOrderFilesData = data?.getWorkOrderFiles_V1.data as GetWorkOrderFilesPayloadModel[] | null | undefined;

  useLogoutOnApolloError(useExternalData ? undefined : internalError);

  useEffect(() => {
    if (!workOrderFilesData) {
      return;
    }

    const convertedData = transformToTreeNode(workOrderFilesData, t);

    setStructure(convertedData);
  }, [t, workOrderFilesData]);

  if (isLoading) {
    return <SkeletonComponent height={'100vh'} width={'100%'} />;
  }

  return (
    <FolderTreeView
      currentUser={currentUser}
      initialFolderId={fileFilters?.folderId}
      isLoadingWorkOrderData={isLoadingWorkOrderData}
      isMonday={isMonday}
      isShowingSideBar={isShowingSideBar ?? true}
      orignalData={workOrderFilesData}
      refetch={refetch}
      structure={structure}
      workOrderData={workOrderData}
      workOrderId={workOrderId}
    />
  );
};
